import { InfiniteData, useMutation, useQueryClient } from '@tanstack/react-query';

import { useCallback, useEffect } from 'react';

import { Workspace } from '@/features/user-settings/types/workspace-types';

import { useAppStore } from '@/shared/contexts/app-wrapper/providers/app-provider';
import { AppWrapperStoreState } from '@/shared/contexts/app-wrapper/stores/app-wrapper-store';
import { ISUserFormsResponse, TFormStatus } from '@/shared/services/forms/forms.types';
import { saveUserFilter } from '@/shared/services/user';
import { ISaveUserFilterPayload } from '@/shared/services/user/user.types';

import { GET_USER_FORMS_QUERY_KEY } from '@/utils/constants';

import { useFormDashboardStore } from '../components/common/form-dashboard-provider';
import { initialFilters } from '../store/form-dashboard-store';
import { FormDashboardFilters } from '../types/home-store-types';

/**
 * Serialize filters to string for API storage
 */
function serializeFilters(filters: FormDashboardFilters): string {
	const parts: string[] = [];

	if (filters.search) parts.push(`search:${filters.search}`);
	if (filters.createdByMe) parts.push('createdByMe:true');
	if (filters.liveForms) parts.push('liveForms:true');
	if (filters.closedForms) parts.push('closedForms:true');

	return parts.join(',');
}

/**
 * Deserialize string from API to filters object
 */
function deserializeFilters(filterString: string): FormDashboardFilters {
	if (!filterString) return initialFilters;

	const filters = { ...initialFilters };
	const parts = filterString.split(',');

	parts.forEach((part) => {
		const [key, value] = part.split(':');
		if (key === 'search') filters.search = value || '';
		if (key === 'createdByMe') filters.createdByMe = value === 'true';
		if (key === 'liveForms') filters.liveForms = value === 'true';
		if (key === 'closedForms') filters.closedForms = value === 'true';
	});

	return filters;
}

/**
 * Hook to manage dashboard filters via API
 * Saves user's filter preferences to backend per workspace
 */
export function useDashboardFilters() {
	const filters = useFormDashboardStore((state) => state.filters);
	const setFilters = useFormDashboardStore((state) => state.setFilters);
	const resetFilters = useFormDashboardStore((state) => state.resetFilters);

	const selectedWorkspaceId = useAppStore(
		(state: AppWrapperStoreState) => state.selectedWorkspaceId
	);
	const workspaces = useAppStore((state: AppWrapperStoreState) => state.workspaces);
	const updateWorkspaceSavedFilter = useAppStore(
		(state: AppWrapperStoreState) => state.updateWorkspaceSavedFilter
	);

	// Load saved filters from workspace data on mount (only once)
	useEffect(() => {
		if (!workspaces || !selectedWorkspaceId) return;

		const workspace = workspaces.find((ws: Workspace) => ws.id === selectedWorkspaceId);
		if (workspace?.savedFilter) {
			const savedFilters = deserializeFilters(workspace.savedFilter);
			setFilters(savedFilters);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedWorkspaceId]); // Only run when workspace changes, not when workspaces array updates

	// Mutation to save filters to API
	const saveFilterMutation = useMutation<string, Error, ISaveUserFilterPayload>({
		mutationFn: saveUserFilter,
		onError: (error) => {
			console.error('Failed to save filter:', error);
		},
	});

	const { mutate: saveFilter, isPending: isSaving } = saveFilterMutation;

	// Update filters and save to API
	const updateFilters = useCallback(
		(updates: Partial<FormDashboardFilters>) => {
			// Get current filters and merge with updates
			const currentFilters = filters;
			const newFilters = { ...currentFilters, ...updates };

			// Update store immediately
			setFilters(updates);

			// Don't persist search to saved filters — it's ephemeral
			if ('search' in updates && Object.keys(updates).length === 1) return;

			// Save to API if workspace is selected
			if (selectedWorkspaceId) {
				const filterString = serializeFilters({ ...newFilters, search: '' });

				updateWorkspaceSavedFilter(selectedWorkspaceId, filterString);

				saveFilter({
					page_id: selectedWorkspaceId,
					saved_filter: filterString,
				});
			}
		},
		[filters, setFilters, selectedWorkspaceId, saveFilter, updateWorkspaceSavedFilter]
	);

	// Reset filters and save to API
	const resetFiltersAndSave = useCallback(() => {
		// Reset store
		resetFilters();

		// Save empty filter to API if workspace is selected
		if (selectedWorkspaceId) {
			updateWorkspaceSavedFilter(selectedWorkspaceId, '');

			saveFilter({
				page_id: selectedWorkspaceId,
				saved_filter: '',
			});
		}
	}, [resetFilters, selectedWorkspaceId, saveFilter, updateWorkspaceSavedFilter]);

	return {
		filters,
		updateFilters,
		resetFilters: resetFiltersAndSave,
		isSaving,
	};
}

type FormsQueryData = InfiniteData<ISUserFormsResponse> | undefined;

/**
 * Hook to manage form status updates in the query cache
 * Provides optimistic updates for form status changes
 */
export function useFormStatusCache() {
	const queryClient = useQueryClient();
	const filters = useFormDashboardStore((state) => state.filters);

	/**
	 * Get previous data snapshot for rollback
	 */
	const getPreviousData = useCallback(() => {
		const previousData: [readonly unknown[], FormsQueryData][] = [];
		queryClient
			.getQueriesData<FormsQueryData>({ queryKey: [GET_USER_FORMS_QUERY_KEY] })
			.forEach(([key, data]) => {
				if (data) previousData.push([key, data]);
			});
		return previousData;
	}, [queryClient]);

	/**
	 * Rollback to previous data on error
	 */
	const rollbackToPreviousData = useCallback(
		(previousData: [readonly unknown[], FormsQueryData][]) => {
			previousData.forEach(([key, data]) => {
				queryClient.setQueryData(key, data);
			});
		},
		[queryClient]
	);

	/**
	 * Optimistically update form status in the query cache
	 */
	const updateFormStatusInCache = useCallback(
		async (formId: string, newStatus: TFormStatus) => {
			// Cancel outgoing refetches to avoid overwriting optimistic update
			await queryClient.cancelQueries({ queryKey: [GET_USER_FORMS_QUERY_KEY] });

			// Check if form should be removed from current filtered view
			const shouldRemoveFromView =
				(filters.liveForms && newStatus === 'CLOSED') ||
				(filters.closedForms && newStatus === 'PUBLISHED');

			// Optimistically update cache
			queryClient.setQueriesData<FormsQueryData>(
				{ queryKey: [GET_USER_FORMS_QUERY_KEY], exact: false },
				(oldData) => {
					if (!oldData?.pages) return oldData;
					return {
						...oldData,
						pages: oldData.pages.map((page) => ({
							...page,
							forms: shouldRemoveFromView
								? page.forms.filter((form) => form.identifier !== formId)
								: page.forms.map((form) =>
										form.identifier === formId ? { ...form, status: newStatus } : form
									),
						})),
					};
				}
			);
		},
		[queryClient, filters]
	);

	return {
		getPreviousData,
		rollbackToPreviousData,
		updateFormStatusInCache,
	};
}
